import { Component, OnInit, inject, ChangeDetectionStrategy, Signal, input, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { CmsImageData } from '@fcom/core-api';
import { AspectRatios, FetchPriorityEnum } from '@fcom/ui-components';
import { FinnairThemeService, MediaQueryService } from '@fcom/common';
import { BookingLocationCodes } from '@fcom/common/interfaces/booking';
import { ContentType } from '@fcom/common/interfaces';
import { TripType } from '@fcom/core/interfaces';
import { LocationPair } from '@fcom/common/store';
import { MonitoringLogger } from '@fcom/core';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

type AllowedAspectRatios = keyof typeof AspectRatios;

/**
 * Full Width Booking Widget
 *
 * @example
 * <cms-full-width-booking-widget
 *   [backgroundImage]="CmsImageData"
 *   [originCode]="'BER'"
 *   [destinationCode]="'HEL'"
 *   [enableAward]="true"
 *   [enableAurinkomatkat]="true"
 *   [enablePromoCode]="true"
 * ></cms-full-width-booking-widget>
 */
@Component({
  selector: 'cms-full-width-booking-widget',
  templateUrl: './cms-full-width-booking-widget.component.html',
  styleUrls: ['./cms-full-width-booking-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CmsFullWidthBookingWidgetComponent implements OnInit {
  protected readonly FetchPriorityEnum = FetchPriorityEnum;
  private mediaQueryService = inject(MediaQueryService);
  private monitoringLogger = inject(MonitoringLogger);
  private themeService = inject(FinnairThemeService);

  private readonly isMobile: Signal<boolean> = toSignal(this.mediaQueryService.isBreakpoint$('mobile'), {
    initialValue: false,
  });
  private readonly isTablet: Signal<boolean> = toSignal(this.mediaQueryService.isBreakpoint$('tablet'), {
    initialValue: false,
  });

  backgroundImage = input<CmsImageData>();
  contentType = input<ContentType>(ContentType.SINGLE_DESTINATION);
  enableAward = input<boolean>(false);
  enableAurinkomatkat = input<boolean>(false);
  enablePromoCode = input<boolean>(false);
  enableAutoFocus = input<boolean>(true);
  enableOpenJaw = input<boolean>(false);
  initialTripType = input<TripType>();
  locations = input<BookingLocationCodes[]>([]);
  headingEnabled = input<boolean>(true);
  enableBackgroundImage = input<boolean>(true);
  enableFlightTab = input<boolean>(true);
  enableHotelsTab = input<boolean>(false);
  enableCarRentalTab = input<boolean>(false);

  readonly isNewBrand = computed<boolean>(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  readonly aspectRatio = computed<AllowedAspectRatios>(() => {
    if (this.isMobile()) {
      return '1x1';
    }

    if (this.isTablet()) {
      return '4x3';
    }

    return '16x9';
  });

  defaultLocationPairs = computed<LocationPair[]>(() =>
    this.locations().map(
      (ond: BookingLocationCodes) =>
        ({
          origin: ond?.originLocationCode ? { locationCode: ond.originLocationCode } : undefined,
          destination: ond?.destinationLocationCode ? { locationCode: ond.destinationLocationCode } : undefined,
        }) as LocationPair
    )
  );

  ngOnInit(): void {
    if (this.initialTripType() && !Object.values(TripType).includes(this.initialTripType())) {
      this.monitoringLogger.error(
        `Unexpected initialTripType: ${this.initialTripType()} when rendering the booking widget in a content page`
      );
    }
  }
}
